import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { TbEdit, TbTrash, TbPlus, TbDeviceFloppy } from 'react-icons/tb';
import { useAuth } from '../../services/check_login';
import '../../../assets/css/role-management.css';

const RoleManagement = () => {
  const { user: currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [usersWithRole, setUsersWithRole] = useState([]);
  const [brands, setBrands] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    allowedBrands: [],
    allowedStatuses: [],
    permissions: {
      canCreateSMS: false,
      canEditSMS: false,
      canDeleteSMS: false,
      canViewSMS: false,
      canAITranslateSMS: false,
      canCreateEmail: false,
      canEditEmail: false,
      canDeleteEmail: false,
      canViewEmail: false,
      canAITranslateEmail: false,
      canCreateEmailTemplates: false,
      canEditEmailTemplates: false,
      canDeleteEmailTemplates: false,
      canCreatePopup: false,
      canEditPopup: false,
      canDeletePopup: false,
      canViewPopup: false,
      canAITranslatePopup: false,
      canCreatePushNotification: false,
      canEditPushNotification: false,
      canDeletePushNotification: false,
      canViewPushNotification: false,
      canAITranslatePushNotification: false,
      canViewLibrary: false,
      canViewHistory: false,
      canRestoreHistory: false,
      canCopyHTML: false,
      canExtractContent: false,
      canManageLanguages: false,
      canManageBrands: false,
      canDeleteBrands: false,
      canUseAIAssistant: false,
      canCreatePlaceholders: false,
      canEditPlaceholders: false,
      canDeletePlaceholders: false,
      canManageRoles: false,
      canManageUsers: false,
      canManageCampaigns: false,
      canManageIntegrations: false,
      canPushToCRM: false,
      canViewAnalytics: false,
      canExportReports: false,
      canBulkDelete: false,
      canBulkEdit: false,
      canBulkDuplicate: false
    }
  });

  // Check permissions
  const canManageRoles = currentUser?.role?.permissions?.canManageRoles || currentUser?.isAdmin;

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  useEffect(() => {
    fetchRoles();
    fetchBrands();
    fetchStatuses();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/roles/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      console.error('Error response:', error.response?.data);
      showNotification(error.response?.data?.error || 'Failed to fetch roles. Please check if you have admin permissions.', 'error');
    }
  };

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/brand/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/statuses/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatuses(response.data || []);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const handleCreateRole = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      allowedBrands: [],
      allowedStatuses: [],
      permissions: {
        canCreateSMS: false,
        canEditSMS: false,
        canDeleteSMS: false,
        canViewSMS: false,
        canCreateEmail: false,
        canEditEmail: false,
        canDeleteEmail: false,
        canViewEmail: false,
        canCreateEmailTemplates: false,
        canEditEmailTemplates: false,
        canDeleteEmailTemplates: false,
        canCreatePopup: false,
        canEditPopup: false,
        canDeletePopup: false,
        canViewPopup: false,
        canCreatePushNotification: false,
        canEditPushNotification: false,
        canDeletePushNotification: false,
        canViewPushNotification: false,
        canViewLibrary: false,
        canViewHistory: false,
        canRestoreHistory: false,
        canManageLanguages: false,
        canManageBrands: false,
        canDeleteBrands: false,
        canUseAIAssistant: false,
        canCreatePlaceholders: false,
        canEditPlaceholders: false,
        canDeletePlaceholders: false,
        canManageRoles: false,
        canManageUsers: false,
        canManageCampaigns: false,
        canManageIntegrations: false,
        canPushToCRM: false,
        canViewAnalytics: false,
        canExportReports: false,
        canBulkDelete: false,
        canBulkEdit: false,
        canBulkDuplicate: false
      }
    });
    setIsModalOpen(true);
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      allowedBrands: (role.allowedBrands || []).map(b => (typeof b === 'string' ? b : b._id)),
      allowedStatuses: (role.allowedStatuses || []).map(s => (typeof s === 'string' ? s : s._id)),
      permissions: {
        canCreateSMS: role.permissions.canCreateSMS || false,
        canEditSMS: role.permissions.canEditSMS || false,
        canDeleteSMS: role.permissions.canDeleteSMS || false,
        canViewSMS: role.permissions.canViewSMS || false,
        canAITranslateSMS: role.permissions.canAITranslateSMS || false,
        canCreateEmail: role.permissions.canCreateEmail || false,
        canEditEmail: role.permissions.canEditEmail || false,
        canDeleteEmail: role.permissions.canDeleteEmail || false,
        canViewEmail: role.permissions.canViewEmail || false,
        canAITranslateEmail: role.permissions.canAITranslateEmail || false,
        canCreateEmailTemplates: role.permissions.canCreateEmailTemplates || false,
        canEditEmailTemplates: role.permissions.canEditEmailTemplates || false,
        canDeleteEmailTemplates: role.permissions.canDeleteEmailTemplates || false,
        canCreatePopup: role.permissions.canCreatePopup || false,
        canEditPopup: role.permissions.canEditPopup || false,
        canDeletePopup: role.permissions.canDeletePopup || false,
        canViewPopup: role.permissions.canViewPopup || false,
        canAITranslatePopup: role.permissions.canAITranslatePopup || false,
        canCreatePushNotification: role.permissions.canCreatePushNotification || false,
        canEditPushNotification: role.permissions.canEditPushNotification || false,
        canDeletePushNotification: role.permissions.canDeletePushNotification || false,
        canViewPushNotification: role.permissions.canViewPushNotification || false,
        canAITranslatePushNotification: role.permissions.canAITranslatePushNotification || false,
        canViewLibrary: role.permissions.canViewLibrary || false,
        canViewHistory: role.permissions.canViewHistory || false,
        canRestoreHistory: role.permissions.canRestoreHistory || false,
        canCopyHTML: role.permissions.canCopyHTML || false,
        canExtractContent: role.permissions.canExtractContent || false,
        canManageLanguages: role.permissions.canManageLanguages || false,
        canManageBrands: role.permissions.canManageBrands || false,
        canDeleteBrands: role.permissions.canDeleteBrands || false,
        canUseAIAssistant: role.permissions.canUseAIAssistant || false,
        canCreatePlaceholders: role.permissions.canCreatePlaceholders || false,
        canEditPlaceholders: role.permissions.canEditPlaceholders || false,
        canDeletePlaceholders: role.permissions.canDeletePlaceholders || false,
        canManageRoles: role.permissions.canManageRoles || false,
        canManageUsers: role.permissions.canManageUsers || false,
        canManageCampaigns: role.permissions.canManageCampaigns || false,
        canManageIntegrations: role.permissions.canManageIntegrations || false,
        canPushToCRM: role.permissions.canPushToCRM || false,
        canViewAnalytics: role.permissions.canViewAnalytics || false,
        canExportReports: role.permissions.canExportReports || false,
        canBulkDelete: role.permissions.canBulkDelete || false,
        canBulkEdit: role.permissions.canBulkEdit || false,
        canBulkDuplicate: role.permissions.canBulkDuplicate || false
      }
    });
    setIsModalOpen(true);
  };

  const handleDeleteRole = async (role) => {
    try {
      const token = localStorage.getItem('token');
      // Check if any users have this role
      const usersResponse = await axios.get('/api/users/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const usersWithThisRole = usersResponse.data.filter(u => u.role?._id === role._id);
      
      if (usersWithThisRole.length > 0) {
        setUsersWithRole(usersWithThisRole);
        setRoleToDelete(role);
        setIsDeleteModalOpen(true);
        return;
      }
      
      // If no users, proceed with deletion
      setRoleToDelete(role);
      setIsDeleteModalOpen(true);
    } catch (error) {
      console.error('Error checking users:', error);
      showNotification('Failed to check role usage', 'error');
    }
  };

  const confirmDeleteRole = async () => {
    if (!roleToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/roles/${roleToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('Role deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);
      setUsersWithRole([]);
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      showNotification(error.response?.data?.error || 'Failed to delete role', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      if (editingRole) {
        await axios.put(`/api/roles/${editingRole._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Role updated successfully', 'success');
      } else {
        await axios.post('/api/roles/create', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showNotification('Role created successfully', 'success');
      }
      setIsModalOpen(false);
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      showNotification(error.response?.data?.error || 'Failed to save role', 'error');
    }
  };

  const handlePermissionChange = (permission) => {
    const newPermissions = {
      ...formData.permissions,
      [permission]: !formData.permissions[permission]
    };

    // Auto-enable canViewHistory when canRestoreHistory is enabled
    if (permission === 'canRestoreHistory' && !formData.permissions[permission]) {
      newPermissions.canViewHistory = true;
    }

    setFormData({
      ...formData,
      permissions: newPermissions
    });
  };

  const handleBrandToggle = (brandId) => {
    setFormData(prev => ({
      ...prev,
      allowedBrands: prev.allowedBrands.includes(brandId)
        ? prev.allowedBrands.filter(id => id !== brandId)
        : [...prev.allowedBrands, brandId]
    }));
  };

  const handleStatusToggle = (statusId) => {
    setFormData(prev => ({
      ...prev,
      allowedStatuses: prev.allowedStatuses.includes(statusId)
        ? prev.allowedStatuses.filter(id => id !== statusId)
        : [...prev.allowedStatuses, statusId]
    }));
  };

  const handleSelectAllInGroup = (permissions) => {
    const allSelected = permissions.every(p => formData.permissions[p]);
    const newPermissions = { ...formData.permissions };
    permissions.forEach(p => {
      newPermissions[p] = !allSelected;
    });

    // Auto-enable canViewHistory when canRestoreHistory is enabled in Select All
    if (permissions.includes('canRestoreHistory') && !allSelected) {
      newPermissions.canViewHistory = true;
    }

    setFormData({ ...formData, permissions: newPermissions });
  };

  const permissionGroups = {
    '💬 SMS': ['canCreateSMS', 'canEditSMS', 'canDeleteSMS', 'canViewSMS', 'canAITranslateSMS'],
    '📧 Email': ['canCreateEmail', 'canEditEmail', 'canDeleteEmail', 'canViewEmail', 'canAITranslateEmail'],
    '📝 Email Templates Builder': ['canCreateEmailTemplates', 'canEditEmailTemplates', 'canDeleteEmailTemplates'],
    '🔔 Popup': ['canCreatePopup', 'canEditPopup', 'canDeletePopup', 'canViewPopup', 'canAITranslatePopup'],
    '📱 Push Notification': ['canCreatePushNotification', 'canEditPushNotification', 'canDeletePushNotification', 'canViewPushNotification', 'canAITranslatePushNotification'],
    '📚 Library': ['canViewLibrary'],
    '🕐 History': ['canViewHistory', 'canRestoreHistory'],
    '📤 Export': ['canCopyHTML', 'canExtractContent'],
    '⚙️ Settings': ['canManageLanguages', 'canManageBrands', 'canDeleteBrands'],
    '🔖 Placeholders': ['canCreatePlaceholders', 'canEditPlaceholders', 'canDeletePlaceholders'],
    '🤖 AI Assistant': ['canUseAIAssistant'],
    '📊 Analytics': ['canViewAnalytics', 'canExportReports'],
    '🗂️ Bulk Actions': ['canBulkDelete', 'canBulkEdit', 'canBulkDuplicate'],
    '👥 Administration': ['canManageRoles', 'canManageUsers'],
    '📅 Marketing Campaigns': ['canManageCampaigns'],
    '🔌 CRM Integrations': ['canManageIntegrations', 'canPushToCRM']
  };

  const formatPermissionName = (permission) => {
    const names = {
      'canCreateSMS': 'Create',
      'canEditSMS': 'Edit',
      'canDeleteSMS': 'Delete',
      'canViewSMS': 'View',
      'canAITranslateSMS': 'AI Translate',
      'canCreateEmail': 'Create',
      'canEditEmail': 'Edit',
      'canDeleteEmail': 'Delete',
      'canViewEmail': 'View',
      'canAITranslateEmail': 'AI Translate',
      'canCreateEmailTemplates': 'Create',
      'canEditEmailTemplates': 'Edit',
      'canDeleteEmailTemplates': 'Delete',
      'canCreatePopup': 'Create',
      'canEditPopup': 'Edit',
      'canDeletePopup': 'Delete',
      'canViewPopup': 'View',
      'canAITranslatePopup': 'AI Translate',
      'canCreatePushNotification': 'Create',
      'canEditPushNotification': 'Edit',
      'canDeletePushNotification': 'Delete',
      'canViewPushNotification': 'View',
      'canAITranslatePushNotification': 'AI Translate',
      'canViewLibrary': 'View',
      'canViewHistory': 'View',
      'canRestoreHistory': 'Restore',
      'canCopyHTML': 'Copy HTML',
      'canExtractContent': 'Extract Content',
      'canManageLanguages': 'Manage Languages',
      'canManageBrands': 'Manage Brands',
      'canDeleteBrands': 'Delete Brands',
      'canCreatePlaceholders': 'Create',
      'canEditPlaceholders': 'Edit',
      'canDeletePlaceholders': 'Delete',
      'canUseAIAssistant': 'Use AI Assistant',
      'canViewAnalytics': 'View Analytics',
      'canExportReports': 'Export Reports',
      'canBulkDelete': 'Bulk Delete',
      'canBulkEdit': 'Bulk Edit',
      'canBulkDuplicate': 'Bulk Duplicate',
      'canManageRoles': 'Manage Roles',
      'canManageUsers': 'Manage Users',
      'canManageCampaigns': 'Manage Campaigns',
      'canManageIntegrations': 'Manage Integrations',
      'canPushToCRM': 'Push Templates to CRM'
    };
    return names[permission] || permission;
  };

  return (
    <div className="role-management">
      <div className="role-header">
        <h1>Role Management</h1>
        {canManageRoles && (
          <button className="btn-create-role" onClick={handleCreateRole}>
            <TbPlus /> Create New Role
          </button>
        )}
      </div>

      {!canManageRoles && (
        <div className="permission-warning">
          <p>⚠️ You don't have permission to manage roles. You can only view the list.</p>
        </div>
      )}

      <div className="roles-list">
        {roles.map((role) => (
          <motion.div
            key={role._id}
            className="role-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="role-card-header">
              <div>
                <h3>{role.name}</h3>
                {role.isSystem && <span className="system-badge">System Role</span>}
              </div>
              {canManageRoles && !role.isSystem && (
                <div className="role-actions">
                  <button
                    onClick={() => handleEditRole(role)}
                    className="btn-role-modify"
                    title="Edit"
                  >
                    <TbEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteRole(role)}
                    className="btn-role-remove"
                    title="Delete"
                  >
                    <TbTrash />
                  </button>
                </div>
              )}
            </div>
            <p className="role-description">{role.description}</p>
            <div className="role-permissions-summary">
              <strong>Permissions:</strong> {
                Object.values(role.permissions).filter(p => p).length
              } granted
            </div>
            <div className="role-permissions-summary">
              <strong>Brands:</strong> {
                role.allowedBrands && role.allowedBrands.length > 0
                  ? `${role.allowedBrands.length} restricted`
                  : 'All brands'
              }
            </div>
            <div className="role-permissions-summary">
              <strong>Statuses:</strong> {
                role.allowedStatuses && role.allowedStatuses.length > 0
                  ? `${role.allowedStatuses.length} restricted`
                  : 'All statuses'
              }
            </div>
          </motion.div>
        ))}
      </div>

      {canManageRoles && isModalOpen && (
        <div className="modal-overlay">
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <button 
              className="modal-close-button" 
              onClick={() => setIsModalOpen(false)}
              type="button"
            >
              ✕
            </button>
            <h2>{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter role name"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter role description"
                  rows="3"
                />
              </div>

              <div className="permissions-section">
                <div className="permission-group">
                  <div className="permission-group-header">
                    <h4>🏢 Brand Access</h4>
                    {brands.length > 0 && (
                      <button
                        type="button"
                        className="btn-select-all"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          allowedBrands: prev.allowedBrands.length === brands.length
                            ? []
                            : brands.map(b => b._id)
                        }))}
                      >
                        {formData.allowedBrands.length === brands.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <p className="permission-group-hint">
                    Leave all unchecked to allow access to every brand. Check specific brands to restrict this role to only those brands.
                  </p>
                  <div className="permission-checkboxes">
                    {brands.length === 0 ? (
                      <span className="no-brands-hint">No brands yet</span>
                    ) : (
                      brands.map((brand) => (
                        <label key={brand._id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.allowedBrands.includes(brand._id)}
                            onChange={() => handleBrandToggle(brand._id)}
                          />
                          <span>{brand.brandName}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <div className="permission-group">
                  <div className="permission-group-header">
                    <h4>🏷️ Status Access</h4>
                    {statuses.length > 0 && (
                      <button
                        type="button"
                        className="btn-select-all"
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          allowedStatuses: prev.allowedStatuses.length === statuses.length
                            ? []
                            : statuses.map(s => s._id)
                        }))}
                      >
                        {formData.allowedStatuses.length === statuses.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>
                  <p className="permission-group-hint">
                    Leave all unchecked to allow access to every status. Check specific statuses to restrict this role to only those statuses.
                  </p>
                  <div className="permission-checkboxes">
                    {statuses.length === 0 ? (
                      <span className="no-brands-hint">No statuses yet</span>
                    ) : (
                      statuses.map((status) => (
                        <label key={status._id} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.allowedStatuses.includes(status._id)}
                            onChange={() => handleStatusToggle(status._id)}
                          />
                          <span>{status.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>

                <h3>Permissions</h3>
                {Object.entries(permissionGroups).map(([groupName, permissions]) => (
                  <div key={groupName} className="permission-group">
                    <div className="permission-group-header">
                      <h4>{groupName}</h4>
                      <button
                        type="button"
                        className="btn-select-all"
                        onClick={() => handleSelectAllInGroup(permissions)}
                      >
                        {permissions.every(p => formData.permissions[p]) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="permission-checkboxes">
                      {permissions.map((permission) => (
                        <label key={permission} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.permissions[permission]}
                            onChange={() => handlePermissionChange(permission)}
                          />
                          <span>{formatPermissionName(permission)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setIsModalOpen(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  <TbDeviceFloppy /> {editingRole ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="modal-overlay">
          <motion.div
            className="modal-content"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <button 
              className="modal-close-button" 
              onClick={() => setIsDeleteModalOpen(false)}
              type="button"
            >
              ✕
            </button>
            <h2>Delete Role: {roleToDelete?.name}</h2>
            
            {usersWithRole.length > 0 ? (
              <div className="delete-warning-container">
                <p className="delete-warning-message">
                  ⚠️ Cannot delete this role! The following users are assigned to this role:
                </p>
                <div className="users-list-container">
                  <ul className="users-list">
                    {usersWithRole.map(user => (
                      <li key={user._id} className="user-item">
                        <strong>{user.username}</strong> ({user.email})
                        {user.firstName && user.lastName && ` - ${user.firstName} ${user.lastName}`}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="reassign-message">
                  Please reassign or delete these users before deleting this role.
                </p>
                <div className="modal-actions">
                  <button 
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setRoleToDelete(null);
                      setUsersWithRole([]);
                    }} 
                    className="btn-cancel"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="delete-confirmation-container">
                <p className="delete-confirmation-message">
                  Are you sure you want to delete the role <strong>{roleToDelete?.name}</strong>?
                </p>
                <div className="modal-actions">
                  <button 
                    onClick={() => {
                      setIsDeleteModalOpen(false);
                      setRoleToDelete(null);
                    }} 
                    className="btn-cancel"
                  >
                    Cancel
                  </button>
                  <button onClick={confirmDeleteRole} className="btn-delete">
                    <TbTrash /> Delete Role
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`notification ${notification.type}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '15px 25px',
            borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#4CAF50' : '#f44336',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {notification.message}
        </motion.div>
      )}
    </div>
  );
};

export default RoleManagement;
